import { Formik, Form } from "formik";
import { toast } from "react-toastify";
import { useRecoilValue } from "recoil";
import React, { useState } from "react";
import PatientInfo from "./Forms/PatientInfo";
import PatientFill from "./Forms/PatientFill";
import useStyles from "../../../styles/styles";
import PatientAddress from "./Forms/PatientAddress";
import PatientAccount from "./Forms/PatientAccount";
import { configState } from "../../../recoil/atoms";
import PatientSummary from "./Forms/PatientSummary";
import PatientInsurance from "./Forms/PatientInsurance";
import DuplicateInfoPatient from "./Forms/DuplicateInfoPatient";
import { validationSchema } from "./FormModel/validationSchema";
import MainCard from "../../../styles/ui-component/cards/MainCard";
import initialPatientValues from "./FormModel/initialPatientValues";
import {Stepper, Step, StepLabel, Button, CircularProgress, Box, Typography} from "@mui/material";
import { AddUserPatientRequest, AddUserPatientResponsePatients, PatientApi } from "../../../api/medical-operations";

const steps = ["Info", "Address", "Insurance", "Account", "Patient"];


export default function AddPatient() {
  const classes = useStyles();
  const config = useRecoilValue(configState);

  const [id, setId] = useState<string>("");
  const [message, setMessage] = useState("");
  const [loading, setLoading] = useState(false);
  const [activeStep, setActiveStep] = useState<number>(0);
  const [skipped, setSkipped] = useState(new Set<number>());
  const [allowDuplicate, setAllowDuplicate] = useState<boolean>(false);
  const [valuesPatient, setValuesPatient] = useState<AddUserPatientRequest>()
  const [patients, setPatients] = useState<AddUserPatientResponsePatients[]>([]);

  const currentValidationSchema = validationSchema[activeStep];
  const isLastStep = activeStep === steps.length - 1;

  const renderStepContent = (step: number) => {
    switch (step) {
      case 0:
        return <PatientInfo />;
      case 1:
        return <PatientAddress />;
      case 2:
        return <PatientInsurance />;
      case 3:
        return <PatientAccount />;
      case 4:
        return <PatientFill />;
      case 5:
        if(message === "Patients with the same phone number already exist!" || 
          message === "Patients with the same name already exist!"){
          return <DuplicateInfoPatient handleBack={handleBack} />
        } else {
          return <PatientSummary />;
        }
      default:
        return <div>Not Found</div>;
    }
  };

  const addPatient = (values: AddUserPatientRequest,actions:any) => {
    setLoading(true);
    const primCodeCapital = values.User.Contact.PrimaryPhone.CountryCode.toUpperCase();
    values.User.Contact.PrimaryPhone.CountryCode= primCodeCapital;
    if (values.User.Contact.SecondaryPhone){
      const secondCodeCapital = values.User.Contact.SecondaryPhone!.CountryCode.toUpperCase();
      values.User.Contact.SecondaryPhone!.CountryCode = secondCodeCapital;
    }

    if(skipped.has(1)) delete values.User.Address;
    if(skipped.has(2)) delete values.User.Insurance;

    if(!values.User.Contact?.PrimaryEmail?.Email){
      delete values.User.Contact.PrimaryEmail
    }
    values.AllowDuplicate = allowDuplicate;

    setValuesPatient(values);
    const Patient = new PatientApi(config);
    Patient.addPatientAndUser(values)
      .then((res) => {
        handleNext();
        toast.success(res?.data?.Message!);
        actions.resetForm({});
        setId(res.data.Patients[0].PatientID || ""); 
        setMessage(res.data.Message);
        setPatients(res.data.Patients);                             
      })
      .catch((err) => {
        toast.error(err?.response?.data?.detail);
      })
      .finally(() => {
        setLoading(false);
      });
  };

  const submitForm = (values: AddUserPatientRequest, actions: any) => {
    actions.setSubmitting(false);
    addPatient(values,actions);
  };

  const handleSubmit = (values: any, actions: any) => {
    if (isLastStep) {
      submitForm(values, actions);
    } else {
      handleNext();
      actions.setTouched({});
      actions.setSubmitting(false);
    }
  };

  const isStepOptional = (step: number) => {
    return step === 1 || step === 2;
  };

  const isStepSkipped = (step:number) => {
    return skipped.has(step);
  }

  const handleNext = () => {
    let newSkipped = skipped;
    if (isStepSkipped(activeStep)) {
      newSkipped = new Set(newSkipped.values());
      newSkipped.delete(activeStep);
    }

    setActiveStep((prevActiveStep) => prevActiveStep + 1);
    setSkipped(newSkipped);    
  };

  const handleBack = () => {
    setActiveStep((prevActiveStep) => prevActiveStep - 1);
  };

  const handleSkip = () => {
    if (!isStepOptional(activeStep)) {
      toast.error("You can't skip a step that isn't optional.");
    }
    else {
      setSkipped((prevSkipped)=>prevSkipped.add(activeStep));
      setActiveStep((prevActiveStep) => prevActiveStep + 1);
    }
  }

  return (
    <>
      <Box>
        <MainCard>
          <Stepper activeStep={activeStep} className={classes.stepper}>
            {steps.map((label, index) => {
              const stepProps: { completed?: boolean } = {};
              const labelProps: {
                optional?: React.ReactNode;
              } = {};
              if (isStepOptional(index)) {
                labelProps.optional = (
                    <Typography variant="caption">Optional</Typography>
                );
              }
              if (isStepSkipped(index)) {
                stepProps.completed = false;
              }
              return (
                  <Step key={label} {...stepProps}>
                    <StepLabel {...labelProps}>{label}</StepLabel>
                  </Step>
              );
            })}
          </Stepper>
          <Box sx={{ pl: 2 }}>
            {activeStep === steps.length ? (
              message === "Patients with the same phone number already exist!" || 
              message === "Patients with the same name already exist!" ? (
                <DuplicateInfoPatient 
                  patients={patients} 
                  handleBack={handleBack} 
                  message={message}
                  valuesPatient={valuesPatient}                  
                  setAllowDuplicate={setAllowDuplicate}
                />
              ) : (
                <PatientSummary id={id} />
              )
            ) : (
              <Formik
                initialValues={initialPatientValues}
                enableReinitialize={true}
                validationSchema={currentValidationSchema}
                onSubmit={handleSubmit}
              >
                {({ isSubmitting }) => (
                  <Form>
                    {renderStepContent(activeStep)}

                    <div className={classes.buttons}>
                      {activeStep !== 0 && (
                        <Button onClick={handleBack} className={classes.button}>
                          Back
                        </Button>
                      )}
                      {isStepOptional(activeStep) && (
                        <Button
                          className={classes.button}
                          sx={{color:"#7F8487"}}
                          onClick={handleSkip}
                        >
                          Skip
                        </Button>
                      )}
                      <div className={classes.wrapper}>
                        <Button
                          disabled={loading}
                          type="submit"
                          variant="contained"
                          color="primary"
                          className={classes.button}
                          startIcon={loading ? <CircularProgress size={20} /> : null}
                        >
                          {isLastStep ? (loading ? "Submitting..." : "Add Patient") : "Next"}                       
                        </Button>  
                        {isSubmitting && <CircularProgress size={24} className={classes.buttonProgress} />}                      
                      </div>
                    </div>
                  </Form>
                )}
              </Formik>
            )}
          </Box>
        </MainCard>
      </Box>
    </>
  );
}
